/**
 * Production-ready reimagined Contribution History component.
 *
 * Renders inside the existing `slr.dashboard` route when the
 * `reimaginedAccountActivity` feature flag is enabled.
 *
 * Strips out: demo mode, DataIndicator badges, "Back to Finance" link,
 * links to reimagined-only routes (optimizer, etc).
 */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { htmlSafe } from '@ember/template';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import { trackedFunction } from 'reactiveweb/function';
import { VStack } from 'tio-ui/components/layout';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import ReSelect from 'tio-reimagined/components/select';
import { Checkbox } from 'tio-ui/components/forms';
import { Heading, Text } from 'tio-ui/components/typography';
import { formatCurrency } from 'tio-reimagined/format';
import PageHero from 'tio-reimagined/components/page-hero';
import StatCard from 'tio-reimagined/components/stat-card';
import SectionHeader from 'tio-reimagined/components/section-header';
import PaymentStatusCard from 'tio-employee/components/reimagined/payment-status-card';
import ContributionTimeline from 'tio-employee/components/reimagined/contribution-timeline';
import PaymentAlertCard from 'tio-employee/components/reimagined/payment-alert-card';
import AccountSelectorDrawer from 'tio-employee/components/reimagined/account-selector-drawer';
import PdfExportDrawer from 'tio-employee/components/reimagined/pdf-export-drawer';
import type { ContributionHistoryData } from 'tio-employee/services/reimagined-data';
import type { EmployerContributions } from 'tio-employee/services/career-pathways-api';
import type SessionContextService from 'tio-employee/services/session-context';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type IntlService from 'ember-intl/services/intl';
import type PersonModel from 'tio-common/models/person';
import type PostalAddressModel from 'tio-common/models/postal-address';
import type RouterService from '@ember/routing/router-service';
import type StoreService from 'ember-data/store';
import {
  PhClockCounterClockwise,
  PhCurrencyDollar,
  PhTrendUp,
  PhCheckCircle,
  PhHandshake,
  PhBank,
  PhArrowRight,
  PhSwap,
  PhFilePdf,
} from 'ember-phosphor-icons';

export interface ReimaginedContributionHistoryModel {
  contributionHistory: ContributionHistoryData;
  employerContributions: {
    contributions: EmployerContributions;
    isLiveData: boolean;
  };
  person: PersonModel;
}

interface ReimaginedContributionHistorySignature {
  Args: {
    model: ReimaginedContributionHistoryModel;
  };
}

export default class ReimaginedContributionHistory extends Component<ReimaginedContributionHistorySignature> {
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;
  @service declare store: StoreService;

  @tracked isAccountDrawerOpen = false;
  @tracked isPdfDrawerOpen = false;
  @tracked showEditAddress = false;
  @tracked editAddressHasSubmitted = false;
  @tracked selectedAddress?: PostalAddressModel;
  @tracked addressConfirmationChecked = false;

  get history() {
    return this.args.model.contributionHistory;
  }

  get contributions() {
    return this.args.model.employerContributions.contributions;
  }

  get employerName() {
    return (
      this.sessionContext.company?.name ||
      this.sessionContext.currentEmployee?.company?.name ||
      'Your employer'
    );
  }

  get heroSubtitle() {
    if (this.history.isSlrEligible && this.history.paymentCount > 0) {
      return `${this.employerName} has sent ${this.history.paymentCount} contributions totaling ${formatCurrency(this.history.lifetimeTotal)} to help pay down your student loans.`;
    }
    return `Track your employer contributions and payment history. ${this.employerName} helps you pay down your student loans faster.`;
  }

  get showPaymentCards() {
    return this.history.isSlrEligible;
  }

  get showAlerts() {
    return this.history.accountAlerts.length > 0 || this.history.paymentValidationErrors.length > 0;
  }

  get currentYearSubtext() {
    return `Contributions in ${new Date().getFullYear()}`;
  }

  get person() {
    return this.args.model.person;
  }

  get activeAccounts() {
    return this.person.activeAccounts;
  }

  get accountReceivingContributions() {
    return this.person.accountReceivingContributions;
  }

  get employeeId() {
    return this.sessionContext.currentEmployee?.id ?? '';
  }

  get isAddressSubmitDisabled() {
    return (
      !this.selectedAddress || this.editAddressHasSubmitted || !this.addressConfirmationChecked
    );
  }

  get selectedAddressId() {
    return this.selectedAddress?.id || '';
  }

  get addressOptions() {
    return (
      // @ts-expect-error
      this.addressData.value?.map((address: PostalAddressModel) => ({
        label: htmlSafe(
          address.addressLine1 +
            (address.addressLine2 ? '\n ' + address.addressLine2 : '') +
            (address.addressLine3 ? '\n ' + address.addressLine3 : '')
        ),
        key: address.id,
        address,
      })) || []
    );
  }

  addressData = trackedFunction(this, async () => {
    // @ts-expect-error
    return await this.store.query('postal-address', {
      filter: {
        financialInstitution: this.person.accountReceivingContributions?.financialInstitution?.id,
        isActive: true,
        type: 'PAYMENT',
      },
      sort: 'address-line1',
      page: {
        offset: 0,
        limit: 400,
      },
    });
  });

  toggleEditAddress = () => {
    this.showEditAddress = !this.showEditAddress;
    this.selectedAddress = undefined;
    this.addressConfirmationChecked = false;
    this.editAddressHasSubmitted = false;
  };

  setSelectedAddress = (id: string | null) => {
    this.selectedAddress = this.addressOptions.find((a) => a.key === id)?.address;
  };

  updateAddress = () => {
    return this.updateAddressTask.perform();
  };

  updateAddressTask = dropTask(async () => {
    this.editAddressHasSubmitted = true;
    const account = this.person.accountReceivingContributions;
    if (!this.selectedAddress || !account) {
      this.editAddressHasSubmitted = false;
      return;
    }
    try {
      const newAddress = this.addressData.value?.find(
        // @ts-expect-error
        (a: PostalAddressModel) => a.id === this.selectedAddress?.id
      );
      if (!newAddress) {
        throw new Error('Address not found');
      }
      account.postalAddress = newAddress as PostalAddressModel;
      await account.save();
      this.toggleEditAddress();
      this.router.refresh('authenticated.slr.dashboard');
      const successMessage = this.intl.t('common.accounts.update_address.success');
      this.notifications.add(successMessage, {
        appearance: 'success',
      });
    } catch (e) {
      console.error(e);
      const errorMessage = this.intl.t('common.accounts.update_address.error');
      this.notifications.add(errorMessage, {
        appearance: 'error',
      });
    } finally {
      this.editAddressHasSubmitted = false;
    }
  });

  toggleAccountDrawer = () => {
    this.isAccountDrawerOpen = !this.isAccountDrawerOpen;
  };

  togglePdfDrawer = () => {
    this.isPdfDrawerOpen = !this.isPdfDrawerOpen;
  };

  onAccountChanged = () => {
    this.router.refresh('authenticated.slr.dashboard');
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      {{! Hero Banner }}
      <PageHero
        @icon={{PhClockCounterClockwise}}
        @title="Contribution History"
        @subtitle={{this.heroSubtitle}}
        @theme="violet"
        role="banner"
      >
        <:stat>
          <div class="text-3xl font-bold tracking-tight">{{formatCurrency
              this.history.lifetimeTotal
            }}</div>
          <Text class="text-violet-200 text-sm font-medium">Lifetime Contributions</Text>
        </:stat>
      </PageHero>

      {{! Eligibility & Account Status }}
      {{#if this.history.isSlrEligible}}
        <div class="flex items-center gap-3 mt-4">
          <div class="flex items-center gap-1.5">
            <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
            <Text class="text-sm text-gray-600">Receiving Contributions</Text>
          </div>
          {{#if this.history.accountReceivingName}}
            <span class="text-gray-300">·</span>
            <div class="flex items-center gap-1.5">
              <PhBank @size={{14}} class="text-gray-400" />
              <Text class="text-sm text-gray-600">{{this.history.accountReceivingName}}</Text>
            </div>
            {{#if this.history.hasMultipleAccounts}}
              <button
                type="button"
                class="text-sm font-medium text-violet-600 hover:text-violet-700 flex items-center gap-1"
                {{on "click" this.toggleAccountDrawer}}
              >
                <PhSwap @size={{14}} />
                Change Account
              </button>
            {{/if}}
          {{/if}}
        </div>
      {{/if}}

      {{! Payment Alerts }}
      {{#if this.showAlerts}}
        <PaymentAlertCard
          @alerts={{this.history.accountAlerts}}
          @validationErrors={{this.history.paymentValidationErrors}}
          @isSlrEligible={{this.history.isSlrEligible}}
          @onSelectAddress={{this.toggleEditAddress}}
          class="mt-4"
        />
      {{/if}}

      {{! Last Payment / Next Payment / Totals Row }}
      {{#if this.showPaymentCards}}
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          {{#if this.history.lastPayment}}
            <PaymentStatusCard
              @title="Last Payment"
              @amount={{this.history.lastPayment.amount}}
              @date={{this.history.lastPayment.paidDate}}
              @status={{this.history.lastPayment.status}}
              @theme="violet"
            />
          {{/if}}

          {{#if this.history.nextPayment}}
            <PaymentStatusCard
              @title="Next Payment"
              @amount={{this.history.nextPayment.amount}}
              @date={{this.history.nextPayment.benefitCycle}}
              @status={{this.history.nextPayment.status}}
              @theme="blue"
            />
          {{/if}}

          <StatCard
            @icon={{PhCurrencyDollar}}
            @label="Year-to-Date"
            @value={{formatCurrency this.history.yearToDate}}
            @subtext={{this.currentYearSubtext}}
            @color="emerald"
          />

          <StatCard
            @icon={{PhTrendUp}}
            @label="Total Payments"
            @value={{this.history.paymentCount}}
            @subtext={{formatCurrency this.history.lifetimeTotal}}
            @color="violet"
          >
            <:extra>
              <div class="flex items-center gap-1 text-emerald-600 text-sm">
                <PhCheckCircle @size={{14}} />
                <span>All time</span>
              </div>
            </:extra>
          </StatCard>
        </div>
      {{/if}}

      {{! Projected Annual Summary }}
      <div class="bg-linear-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white mt-6">
        <div class="flex items-center justify-between flex-wrap gap-4">
          <div class="flex items-center gap-4">
            <div class="p-3 bg-white/20 rounded-lg">
              <PhHandshake @size={{28}} />
            </div>
            <div>
              <Heading @level={{3}} class="text-white text-lg font-semibold">
                {{this.employerName}}
                Contribution Summary
              </Heading>
              <Text class="text-emerald-100">
                Projected
                {{formatCurrency this.contributions.projectedAnnual}}
                this year based on current contribution rate.
              </Text>
            </div>
          </div>
          <div class="flex items-center gap-6">
            <div class="text-center">
              <div class="text-2xl font-bold">{{formatCurrency this.contributions.yearToDate}}</div>
              <Text class="text-emerald-100 text-sm">This Year</Text>
            </div>
            <div class="text-center">
              <div class="text-2xl font-bold">{{formatCurrency this.contributions.lifetime}}</div>
              <Text class="text-emerald-100 text-sm">Lifetime</Text>
            </div>
          </div>
        </div>
      </div>

      {{! Payment History Timeline/Table }}
      <div class="mt-6">
        <ContributionTimeline @payments={{this.history.payments}} />
      </div>

      {{! Quick Actions }}
      <div class="mt-6">
        <SectionHeader
          @icon={{PhTrendUp}}
          @title="Quick Actions"
          @iconClass="text-violet-500"
          @iconSize={{24}}
        />
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button type="button" class="block w-full text-left" {{on "click" this.togglePdfDrawer}}>
            <div
              class="bg-white rounded-xl p-5 shadow-sm ring-1 ring-gray-950/4 hover:shadow-md transition-shadow h-full"
            >
              <div class="flex items-start gap-3">
                <div class="p-2 bg-rose-100 rounded-lg shrink-0">
                  <PhFilePdf @size={{20}} class="text-rose-600" />
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between gap-2">
                    <Text class="font-medium text-gray-900">Export PDF</Text>
                    <PhArrowRight @size={{16}} class="text-gray-400 shrink-0" />
                  </div>
                  <Text @intent="muted" class="text-sm">Download contribution report</Text>
                </div>
              </div>
            </div>
          </button>

          <LinkTo @route="authenticated.account-activity.accounts" class="block h-full">
            <div
              class="bg-white rounded-xl p-5 shadow-sm ring-1 ring-gray-950/4 hover:shadow-md transition-shadow h-full"
            >
              <div class="flex items-start gap-3">
                <div class="p-2 bg-blue-100 rounded-lg shrink-0">
                  <PhBank @size={{20}} class="text-blue-600" />
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between gap-2">
                    <Text class="font-medium text-gray-900">View Loan Accounts</Text>
                    <PhArrowRight @size={{16}} class="text-gray-400 shrink-0" />
                  </div>
                  <Text @intent="muted" class="text-sm">See balances and progress</Text>
                </div>
              </div>
            </div>
          </LinkTo>

          <LinkTo @route="authenticated.observability.upload" class="block h-full">
            <div
              class="bg-white rounded-xl p-5 shadow-sm ring-1 ring-gray-950/4 hover:shadow-md transition-shadow h-full"
            >
              <div class="flex items-start gap-3">
                <div class="p-2 bg-violet-100 rounded-lg shrink-0">
                  <PhTrendUp @size={{20}} class="text-violet-600" />
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between gap-2">
                    <Text class="font-medium text-gray-900">Upload Documents</Text>
                    <PhArrowRight @size={{16}} class="text-gray-400 shrink-0" />
                  </div>
                  <Text @intent="muted" class="text-sm">Update your loan information</Text>
                </div>
              </div>
            </div>
          </LinkTo>
        </div>
      </div>

      {{! Drawers }}
      <AccountSelectorDrawer
        @isOpen={{this.isAccountDrawerOpen}}
        @onClose={{this.toggleAccountDrawer}}
        @accounts={{this.activeAccounts}}
        @currentAccount={{this.accountReceivingContributions}}
        @personId={{this.person.id}}
        @onAccountChanged={{this.onAccountChanged}}
      />

      <PdfExportDrawer
        @isOpen={{this.isPdfDrawerOpen}}
        @onClose={{this.togglePdfDrawer}}
        @employeeId={{this.employeeId}}
      />

      {{! Address Selection Modal }}
      <Modal @isOpen={{this.showEditAddress}} @onClose={{this.toggleEditAddress}} @size="xl">
        <:header>{{t "common.accounts.select_payment_address"}}</:header>
        <:body>
          <VStack class="p-0!">
            <p>{{t "slr.select_address_body_1"}}</p>
            <p class="italic text-gray-500">{{t "slr.select_address_body_2"}}</p>
            <form class="flex flex-col gap-y-4">
              <ReSelect
                @searchable={{true}}
                @clearable={{true}}
                @items={{this.addressOptions}}
                @selected={{this.selectedAddressId}}
                @onChange={{this.setSelectedAddress}}
                @label="Servicer Address"
              />
              <Checkbox
                disabled={{this.updateAddressTask.isRunning}}
                @label="I confirm the selected payment address is correct and understand that incorrect addresses may cause payment to be sent to the wrong address and delays of 90 days or longer."
                @checked={{this.addressConfirmationChecked}}
                @onChange={{fn (mut this.addressConfirmationChecked)}}
              />
            </form>
          </VStack>
        </:body>
        <:footer>
          <Footer
            @isRunning={{this.updateAddressTask.isRunning}}
            @onCancel={{this.toggleEditAddress}}
            @onSubmit={{this.updateAddress}}
            @submitDisabled={{this.isAddressSubmitDisabled}}
          />
        </:footer>
      </Modal>
    </VStack>
  </template>
}
